import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CaseDetail, DisbursementMethod, PresignedUpload } from '@foundation/shared';
import {
  CASE_STATUS_LABEL_TH,
  DISBURSEMENT_METHODS,
  DISBURSEMENT_METHOD_LABEL_TH,
} from '@foundation/shared';
import { api, tokenStore } from '../lib/api';
import { formatDate } from '../lib/format';
import { getCurrentPosition, type Coords } from '../lib/image';
import { uploadPhoto } from '../lib/upload';
import SignaturePad from '../components/SignaturePad';

const baht = (satang: number | null): string =>
  satang === null ? '—' : `${(satang / 100).toLocaleString('th-TH')} บาท`;

interface PresignResponse {
  uploads: PresignedUpload[];
  mode: 'presigned' | 'worker';
}

/** อัปโหลดลายเซ็นที่วาดบนจอ — คนละ purpose กับรูปงานลงพื้นที่ */
async function uploadSignature(blob: Blob, caseId: string): Promise<string> {
  const presign = await api<PresignResponse>('/uploads/presign', {
    method: 'POST',
    body: { purpose: 'signature', refId: caseId, count: 1, contentType: 'image/png' },
  });
  const target = presign.uploads[0];
  if (!target) throw new Error('ขอที่อยู่สำหรับอัปโหลดลายเซ็นไม่สำเร็จ');

  const headers: Record<string, string> = { 'content-type': 'image/png' };
  if (presign.mode === 'worker') {
    const token = tokenStore.get();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(target.url, { method: 'PUT', headers, body: blob });
  if (!res.ok) throw new Error(`อัปโหลดลายเซ็นไม่สำเร็จ (${res.status})`);
  return target.key;
}

export default function CaseFlow() {
  const { id = '' } = useParams();
  const [data, setData] = useState<CaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'disburse'>('view');

  async function load() {
    setError(null);
    try {
      setData(await api<CaseDetail>(`/cases/${id}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดเคสไม่สำเร็จ');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error && !data) return <div className="screen alert error">{error}</div>;
  if (!data) return <div className="center">กำลังโหลด…</div>;

  const canDisburse = ['approved', 'in_progress'].includes(data.status);

  if (mode === 'disburse') {
    return (
      <DisburseForm
        detail={data}
        onCancel={() => setMode('view')}
        onDone={() => {
          setMode('view');
          void load();
        }}
      />
    );
  }

  return (
    <div className="screen">
      <div className="topbar">
        <Link to="/cases" className="ghost">
          ← เคสของฉัน
        </Link>
        <h1>{data.caseCode}</h1>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>{data.title}</strong>
          <span className="badge">{CASE_STATUS_LABEL_TH[data.status]}</span>
        </div>
        <div className="muted small">
          {data.beneficiaryName} · {data.benCode}
        </div>
        {data.description && <p className="small">{data.description}</p>}

        <dl className="kv">
          <dt>ประเภท</dt>
          <dd>{data.aidTypeName}</dd>
          <dt>อนุมัติแล้ว</dt>
          <dd>{baht(data.approvedAmount)}</dd>
          <dt>จ่ายไปแล้ว</dt>
          <dd>{baht(data.disbursedAmount)}</dd>
          <dt>คงเหลือ</dt>
          <dd>
            <strong>{baht(data.remainingAmount)}</strong>
          </dd>
          {data.dueDate && (
            <>
              <dt>กำหนดเสร็จ</dt>
              <dd>{formatDate(data.dueDate)}</dd>
            </>
          )}
        </dl>
      </div>

      {data.disbursements.length > 0 && (
        <div className="card">
          <h2 className="small">การจ่ายที่บันทึกไว้</h2>
          {data.disbursements.map((d) => (
            <div key={d.id} className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <div>{baht(d.amount)}</div>
                <div className="muted small">
                  {DISBURSEMENT_METHOD_LABEL_TH[d.method]}
                  {d.goodsDetail ? ` · ${d.goodsDetail}` : ''}
                </div>
              </div>
              <div className="small">
                {d.hasSignature && <span className="badge green">ลายเซ็น</span>}
                {d.verifiedAt && <span className="badge">ตรวจรับแล้ว</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {canDisburse ? (
        <button onClick={() => setMode('disburse')}>บันทึกการจ่ายความช่วยเหลือ</button>
      ) : (
        <div className="alert info">
          {data.status === 'submitted' || data.status === 'under_review'
            ? 'เคสนี้ยังรอการอนุมัติ — จ่ายได้เมื่ออนุมัติแล้ว'
            : 'เคสนี้ปิดแล้ว บันทึกการจ่ายเพิ่มไม่ได้'}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- ฟอร์มจ่าย */

function DisburseForm({
  detail,
  onCancel,
  onDone,
}: {
  detail: CaseDetail;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(String(detail.remainingAmount / 100));
  const [method, setMethod] = useState<DisbursementMethod>('cash');
  const [goodsDetail, setGoodsDetail] = useState('');
  const [note, setNote] = useState('');
  const [signature, setSignature] = useState<Blob | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ขอพิกัดตั้งแต่เปิดฟอร์ม — GPS ใช้เวลาจับสัญญาณ ถ้ารอตอนกดบันทึกจะช้า
  useEffect(() => {
    void getCurrentPosition().then(setCoords);
  }, []);

  const satang = Math.round(Number(amount || '0') * 100);
  const overRemaining = detail.approvedAmount !== null && satang > detail.remainingAmount;
  const needsGoods = satang === 0 && !goodsDetail.trim();
  const needsSignature = method === 'cash' && !signature;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      let signatureKey: string | undefined;
      if (signature) {
        setStep('กำลังอัปโหลดลายเซ็น…');
        signatureKey = await uploadSignature(signature, detail.id);
      }

      let photoKey: string | undefined;
      if (photo) {
        setStep('กำลังอัปโหลดรูป…');
        photoKey = (await uploadPhoto(photo, { refId: detail.id, coords })).key;
      }

      setStep('กำลังบันทึก…');
      await api(`/cases/${detail.id}/disbursements`, {
        method: 'POST',
        idempotent: true,
        body: {
          disbursedAt: new Date().toISOString(),
          amount: satang,
          method,
          goodsDetail: goodsDetail.trim() || undefined,
          bankAccountId: method === 'transfer' ? 'ba_main' : undefined,
          signatureKey,
          photoKey,
          gpsLat: coords?.lat,
          gpsLng: coords?.lng,
          note: note.trim() || undefined,
        },
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setBusy(false);
      setStep('');
    }
  }

  return (
    <div className="screen">
      <div className="topbar">
        <button className="ghost" onClick={onCancel} disabled={busy}>
          ← ยกเลิก
        </button>
        <h1>บันทึกการจ่าย</h1>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="card">
        <div className="muted small">
          {detail.caseCode} · {detail.beneficiaryName}
        </div>
        <div>คงเหลือที่จ่ายได้ {baht(detail.remainingAmount)}</div>
      </div>

      <div className="field">
        <label htmlFor="amt">จำนวนเงิน (บาท)</label>
        <input
          id="amt"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        {overRemaining && <div className="alert error">เกินวงเงินที่อนุมัติไว้</div>}
        <div className="muted small">ใส่ 0 ได้ถ้ามอบเฉพาะสิ่งของ</div>
      </div>

      <div className="field">
        <label htmlFor="method">วิธีจ่าย</label>
        <select
          id="method"
          value={method}
          onChange={(e) => setMethod(e.target.value as DisbursementMethod)}
        >
          {DISBURSEMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {DISBURSEMENT_METHOD_LABEL_TH[m]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="goods">รายละเอียดสิ่งของ</label>
        <input
          id="goods"
          value={goodsDetail}
          onChange={(e) => setGoodsDetail(e.target.value)}
          placeholder="เช่น ถุงยังชีพ 2 ชุด"
        />
      </div>

      {method === 'cash' && (
        <div className="field">
          <label>ลายเซ็นผู้รับเงิน</label>
          <SignaturePad onChange={setSignature} />
          <div className="muted small">
            จำเป็นสำหรับการจ่ายเงินสด — เป็นหลักฐานว่าเงินถึงมือผู้รับจริง
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="photo">รูปขณะส่งมอบ</label>
        <input
          id="photo"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
        />
        {photo && <div className="muted small">เลือกรูปแล้ว</div>}
      </div>

      <div className="field">
        <label htmlFor="note">บันทึกเพิ่มเติม</label>
        <textarea id="note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <div className="muted small">
        {coords
          ? `พิกัดที่จะบันทึก ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`
          : 'ยังจับพิกัดไม่ได้ — บันทึกได้แต่จะไม่มีพิกัดแนบ'}
      </div>

      <button
        onClick={() => void submit()}
        disabled={busy || overRemaining || needsGoods || needsSignature}
      >
        {busy ? step || 'กำลังบันทึก…' : 'บันทึกการจ่าย'}
      </button>

      {needsSignature && (
        <div className="muted small">ต้องให้ผู้รับเซ็นชื่อก่อนจึงจะบันทึกได้</div>
      )}
      {needsGoods && (
        <div className="muted small">ยอด 0 บาทต้องระบุรายละเอียดสิ่งของที่มอบ</div>
      )}
    </div>
  );
}
